/**
 * The `wizard_question` assistant view (MEIS-13672): one question of a quote's
 * wizard, drawn by the native wizard renderers so the chat offers the same
 * controls (pictures, prices, value tiles, product and template search) as the
 * quote's wizard page. The question is read live through
 * `QuoteWizardQuestions`; the answer travels as a confirming chat message that
 * the model hands to `answer_quote_wizard_question`. Product and template
 * panels save themselves, so their message only reports it (`savedInView`).
 */
import { assertNever } from "@msys/common";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { ApplyItemActionInput } from "../../../../clients/graphqlTypes.js";
import type { PlaceholderPick } from "../../doc-items/selection/placeholderPick.js";
import { PlaceholderSelection } from "../../doc-items/selection/PlaceholderSelection.js";
import {
  useQuoteWizardQuestionsQuery,
  type WizardQuestions_QuestionFragment,
} from "../../documents/WizardQuestions.generated.js";
import {
  getItemQuestionPosition,
  getQuestionPrompt,
  forwardSubmits,
  getQuestions,
} from "../../quotes/wizard/questionShell.js";
import { WizardContingencyRenderer } from "../../quotes/wizard/WizardContingencyRenderer.js";
import { WizardDecisionRenderer } from "../../quotes/wizard/WizardDecisionRenderer.js";
import { WizardPropertiesRenderer } from "../../quotes/wizard/WizardPropertiesRenderer.js";
import { WizardRecommendationsRenderer } from "../../quotes/wizard/WizardRecommendationsRenderer.js";
import type { WizardRendererRef } from "../../quotes/wizard/WizardRendererProps.js";
import type { AgentViewComponentProps } from "./registry.js";
import { useAgentWizardQuestionItemQuery } from "./WizardQuestionView.generated.js";
import {
  buildWizardAnswerMessage,
  itemActionToWizardAnswer,
  type WizardAnswer,
  type WizardQuestionViewSpec,
} from "./wizardQuestionSpec.js";

type Props = Pick<
  AgentViewComponentProps,
  "onSend" | "busy" | "answeredWith"
> & {
  view: WizardQuestionViewSpec;
};

const noop = () => {};

export function WizardQuestionView({
  view,
  onSend,
  busy,
  answeredWith,
}: Props) {
  const { t } = useTranslate(["Global", "Quote"]);
  const stagedRef = React.useRef<WizardRendererRef>(null);
  const [submitted, setSubmitted] = React.useState(false);

  const readOnly = answeredWith !== undefined;
  const locked = readOnly || busy || submitted;

  const questionsQuery = useQuoteWizardQuestionsQuery({
    variables: {
      projectId: view.projectId,
      quoteId: view.quoteId,
      strategy: view.strategy,
    },
    // Read-only views revalidate too, so a stale cached list cannot keep showing
    // a question that is gone; reads of one quote and strategy that are in
    // flight together share a request.
    fetchPolicy: "cache-and-network",
  });
  const questions = React.useMemo(
    () => getQuestions(questionsQuery.data?.quoteWizardQuestions),
    [questionsQuery.data?.quoteWizardQuestions]
  );
  const refused =
    questionsQuery.data !== undefined &&
    questionsQuery.data.quoteWizardQuestions.__typename !==
      "QuoteWizardQuestionsResult";
  // A read-only view whose refresh failed cannot trust its cached list: it
  // shows only the stored prompt, as for a question that left the list.
  const question =
    readOnly && questionsQuery.error
      ? null
      : (questions.find(q => q.questionId === view.questionId) ?? null);

  const isPlaceholder =
    question?.__typename === "WizardProductQuestion" ||
    question?.__typename === "WizardPlaceholderForTemplateQuestion";
  const itemQuery = useAgentWizardQuestionItemQuery({
    variables: {
      docId: view.quoteId,
      itemId: question?.itemId ?? "",
      projectId: view.projectId,
    },
    skip: !isPlaceholder || locked,
  });
  const placeholderItem =
    itemQuery.data?.item.__typename === "Item" ? itemQuery.data.item : null;

  const labels = React.useMemo(() => ({ yes: t("Yes"), no: t("No") }), [t]);

  const send = React.useCallback(
    (
      answer: WizardAnswer,
      from: WizardQuestions_QuestionFragment | null,
      options?: { savedInView?: boolean; pickedTitle?: string | null }
    ) => {
      if (submitted || busy || readOnly) return;
      setSubmitted(true);
      onSend(buildWizardAnswerMessage(view, from, answer, labels, options), {
        confirm: true,
      });
    },
    [submitted, busy, readOnly, onSend, view, labels]
  );

  const onAnswer = React.useCallback(
    async (action: ApplyItemActionInput) => {
      const answer = itemActionToWizardAnswer(action);
      if (answer) send(answer, question);
    },
    [send, question]
  );

  // A failed refresh surfaces as `questionsQuery.error` (the load warning), so
  // the promise is quiet: Retry must not throw, and the panel, which refetches
  // only after its save went through, must still reach `onApplied`.
  const refetchQuestions = questionsQuery.refetch;
  const refetchQuietly = React.useCallback(
    () => refetchQuestions().catch(noop),
    [refetchQuestions]
  );
  // Without notifyOnNetworkStatusChange nothing else shows that Retry runs.
  const [retrying, setRetrying] = React.useState(false);
  const retry = React.useCallback(() => {
    setRetrying(true);
    void refetchQuietly().finally(() => setRetrying(false));
  }, [refetchQuietly]);

  // The panel has saved and awaited the single wizard refetch already; the
  // answer is what it picked, not the refetched list (the question may be gone).
  const onApplied = React.useCallback(
    async (pick: PlaceholderPick) => {
      switch (pick.kind) {
        case "product":
          return send(
            {
              kind: "product",
              product: {
                supplierId: pick.supplierId,
                articleNumber: pick.articleNumber,
              },
            },
            question,
            { savedInView: true, pickedTitle: pick.title }
          );
        case "template":
          return send(
            { kind: "placeholderForTemplate", templateId: pick.templateId },
            question,
            { savedInView: true, pickedTitle: pick.title }
          );
        case "templateContent":
          // The chat passes no item creation, so its panel offers no fill action.
          throw new Error("Unexpected hand-built fill in the assistant chat");
        default:
          assertNever(pick);
      }
    },
    [send, question]
  );

  if (questionsQuery.loading && !questionsQuery.data)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <CircularProgress size={24} />
      </Box>
    );
  // Apollo keeps cached data next to a failed refresh, so an error wins over a
  // cached question: answering it would act on a list we could not re-read.
  // A failed read can be retried; a refusal cannot.
  if (!readOnly && (questionsQuery.error || refused))
    return (
      <Alert
        severity="warning"
        sx={{ py: 0, my: 0.5 }}
        action={
          questionsQuery.error ? (
            <Button
              color="inherit"
              size="small"
              disabled={retrying}
              onClick={retry}
            >
              {t("Retry", { ns: "Global" })}
            </Button>
          ) : undefined
        }
      >
        {t("The wizard questions could not be loaded", { ns: "Quote" })}
      </Alert>
    );

  if (!question) {
    return (
      <Stack spacing={0.5} sx={{ my: 0.5 }}>
        <Typography variant="subtitle2">{view.prompt}</Typography>
        {!readOnly && (
          <Typography variant="body2" color="text.secondary">
            {t("No longer available")}
          </Typography>
        )}
      </Stack>
    );
  }

  const position = getItemQuestionPosition(questions, question.questionId);
  const staged = forwardSubmits(question);

  return (
    <Stack spacing={1.5} sx={{ my: 0.5, width: "100%" }}>
      <Typography variant="subtitle2">{getQuestionPrompt(question)}</Typography>
      {position && position.total > 1 && (
        <Typography variant="caption" color="text.secondary">
          {t("Question {index} of {total}", {
            ns: "Quote",
            index: position.index,
            total: position.total,
          })}
        </Typography>
      )}

      <WizardQuestionBody
        question={question}
        stagedRef={stagedRef}
        onAnswer={onAnswer}
        disabled={locked}
        panel={
          locked ? null : placeholderItem ? (
            <PlaceholderSelection
              key={`${placeholderItem.id}:${question.__typename}`}
              kind={
                question.__typename === "WizardProductQuestion"
                  ? "product"
                  : "template"
              }
              projectId={view.projectId}
              docId={view.quoteId}
              item={placeholderItem}
              expandedItemIds={undefined}
              refetchWizardQuestions={refetchQuietly}
              handleClose={noop}
              onApplied={onApplied}
              embedded
            />
          ) : isPlaceholder ? (
            itemQuery.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Alert severity="warning" sx={{ py: 0, my: 0.5 }}>
                {t("Something went wrong. Please try again.")}
              </Alert>
            )
          ) : null
        }
      />

      {staged && !readOnly && (
        <Box>
          <Button
            variant="contained"
            disabled={locked}
            onClick={() => stagedRef.current?.submit()}
          >
            {t("Next")}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

function WizardQuestionBody({
  question,
  stagedRef,
  onAnswer,
  disabled,
  panel,
}: {
  question: WizardQuestions_QuestionFragment;
  stagedRef: React.RefObject<WizardRendererRef>;
  onAnswer: (action: ApplyItemActionInput) => Promise<void>;
  disabled: boolean;
  panel: React.ReactNode;
}) {
  switch (question.__typename) {
    case "WizardProductQuestion":
    case "WizardPlaceholderForTemplateQuestion":
      return <>{panel}</>;
    case "WizardDecisionQuestion":
      return (
        <WizardDecisionRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    case "WizardMultiplePropertiesQuestion":
      return (
        <WizardPropertiesRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    case "WizardContingencyQuestion":
      return (
        <WizardContingencyRenderer
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    case "WizardMultipleRecommendationsQuestion":
      return (
        <WizardRecommendationsRenderer
          ref={stagedRef}
          question={question}
          onAnswer={onAnswer}
          disabled={disabled}
        />
      );
    default:
      return assertNever(question);
  }
}
